import Link from "next/link";
import { Logo } from "@/components/ui/Logo";

type FooterVariant = "full" | "slim";

interface FooterProps {
  variant?: FooterVariant;
}

export default function Footer({ variant = "full" }: FooterProps) {
  if (variant === "slim") {
    return (
      <footer className="border-t border-border bg-surface">
        <div className="mx-auto max-w-7xl px-4 py-4">
          <nav
            aria-label="Footer links"
            className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-text-light"
          >
            <Link href="/terms" className="hover:text-primary">
              Terms
            </Link>
            <Link href="/refund-policy" className="hover:text-primary">
              Refunds
            </Link>
            <Link href="/trust-safety" className="hover:text-primary">
              Trust &amp; Safety
            </Link>
            <Link href="/contact-us" className="hover:text-primary">
              Contact
            </Link>
            <Link href="/about-us" className="hover:text-primary">
              About
            </Link>
          </nav>
          <p className="mt-3 text-center text-xs text-text-light">
            &copy; {new Date().getFullYear()} Sell It Off. All rights reserved.
          </p>
        </div>
      </footer>
    );
  }

  return (
    <footer className="border-t border-border bg-surface pb-16 lg:pb-0">
      <div className="mx-auto max-w-7xl px-4 py-6 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="mb-4 col-span-1 md:col-span-1">
            <Link href="/" className="inline-flex items-center">
              <Logo width={36} height={36} className="mr-2" />
              <span className="text-2xl font-bold text-primary">
                Sell It Off
              </span>
            </Link>
            <p className="mt-2 text-sm text-text-light max-w-md">
              Your trusted marketplace for quality used or second-hand household
              items in Lagos & Oyo, Nigeria. Buy and sell household items with
              confidence.
            </p>

            <h3 className="mt-4 text-sm font-semibold text-foreground">
              Our Locations
            </h3>
            <p className="mt-2 text-sm text-text-light">Lagos & Oyo, Nigeria</p>
            <p className="text-sm text-text-light">
              Serving all areas including Ikeja, Lekki, Victoria Island, Ikoyi,
              Mainland in Lagos, and Ibadan, Ogbomoso in Oyo
            </p>
          </div>

          <div className="grid grid-cols-2 gap-8 col-span-1 md:col-span-2">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Company</h3>
              <ul className="mt-4 space-y-2">
                <li>
                  <Link
                    href="/about-us"
                    className="text-sm text-text-light hover:text-primary"
                  >
                    About Us
                  </Link>
                </li>
                <li>
                  <Link
                    href="/terms"
                    className="text-sm text-text-light hover:text-primary"
                  >
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link
                    href="/refund-policy"
                    className="text-sm text-text-light hover:text-primary"
                  >
                    Refund Policy
                  </Link>
                </li>
                <li>
                  <Link
                    href="/faq"
                    className="text-sm text-text-light hover:text-primary"
                  >
                    FAQ
                  </Link>
                </li>
                {/* <li>
 <Link href="/careers" className="text-sm text-text-light hover:text-primary">
 Careers
 </Link>
 </li> */}
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-foreground">Support</h3>
              <ul className="mt-4 space-y-2">
                <li>
                  <Link
                    href="/contact-us"
                    className="text-sm text-text-light hover:text-primary"
                  >
                    Contact Us
                  </Link>
                </li>
                <li>
                  <Link
                    href="/trust-safety"
                    className="text-sm text-text-light hover:text-primary"
                  >
                    Trust & Safety
                  </Link>
                </li>
                {/* <li>
 <Link href="/privacy" className="text-sm text-text-light hover:text-primary">
 Privacy Policy
 </Link>
 </li> */}
                {/* <li>
 <Link href="/help" className="text-sm text-text-light hover:text-primary">
 Help Center
 </Link>
 </li> */}
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-6 mt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-xs text-text-light">
            &copy; {new Date().getFullYear()} Sell It Off. All rights reserved.
          </p>
          <div className="mt-4 md:mt-0 flex space-x-6">
            <Link
              href="https://facebook.com/sellitoffnow"
              className="text-text-light hover:text-primary"
            >
              <span className="sr-only">Facebook</span>
              <svg
                className="h-5 w-5"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                  clipRule="evenodd"
                />
              </svg>
            </Link>
            <Link
              href="https://instagram.com/sellitoffnow"
              className="text-text-light hover:text-primary"
            >
              <span className="sr-only">Instagram</span>
              <svg
                className="h-5 w-5"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
                  clipRule="evenodd"
                />
              </svg>
            </Link>
            <Link
              href="https://linkedin.com/company/sellitoffnow"
              className="text-text-light hover:text-primary"
            >
              <span className="sr-only">LinkedIn</span>
              <svg
                className="h-5 w-5"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
